import { useEffect, useState } from 'react'

import { Heart } from 'src/shared/components/icons/heart'
import { ArrowCycle } from 'src/shared/components/icons/referrals/arrow-cycle'
import { Star } from 'src/shared/components/icons/star'
import { Users } from 'src/shared/components/icons/users'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Image } from 'src/shared/components/ui/media/image'
import { ResponsivePopup } from 'src/shared/components/ui/popups/responsive-popup'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUpdateUserTutorials } from 'src/shared/features/users/api/preferences/use-update-user-preferences'
import { useUserTutorials } from 'src/shared/features/users/api/preferences/use-user-tutorials'
import ExampleVisibility from 'src/shared/features/visibility/assets/images/example-visibility-score.png'
import { VisibilityBlankDark } from 'src/shared/features/visibility/assets/visibility-blank-dark'
import { VisibilityDark } from 'src/shared/features/visibility/assets/visibility-dark'

export const VisibilityPopup = () => {
  const { data: tutorials } = useUserTutorials()
  const { data: user } = useMyUserData()
  const updateTutorials = useUpdateUserTutorials()
  const [open, setOpen] = useState(true)
  const [step, setStep] = useState(1)

  const showTutorial = tutorials ? !tutorials.homeTutorial : false

  useEffect(() => {
    if (showTutorial) {
      setOpen(true)
    }
  }, [showTutorial])

  if (!showTutorial) return null

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      updateTutorials.mutate({
        homeTutorial: true,
      })
    }
    setOpen(isOpen)
  }

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1)
    } else {
      handleOpenChange(false)
    }
  }

  const renderStepIndicator = () => {
    return (
      <div className="mb-4 flex cursor-pointer justify-center space-x-2">
        {[1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={`h-2 rounded-full ${index === step ? 'w-12 bg-primary' : 'w-6 bg-muted'}`}
            onClick={() => setStep(index)}
          ></div>
        ))}
      </div>
    )
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col items-center space-y-6 px-6 py-10">
            <VisibilityDark className="size-36" />
            <div className="space-y-3 text-center">
              <h2 className="text-3xl font-bold">Introducing Visibility Score</h2>
              <p className="text-muted">Your visibility score will update at the end of the week</p>
            </div>
            <div className="flex w-full flex-col items-center gap-4">
              <Button onClick={handleNext} className="w-full">
                Next
              </Button>
              {renderStepIndicator()}
            </div>
          </div>
        )
      case 2:
        return (
          <div className="flex flex-col items-center space-y-6 pb-10 pt-16">
            <div className="flex w-full justify-end pl-14">
              <Image src={ExampleVisibility} alt="Example Visibility" className="ml-4 w-full" />
            </div>
            <div className="flex flex-col items-center space-y-6 px-6">
              <div className="space-y-3 text-center">
                <h2 className="text-3xl font-bold">The higher your score is, the more discoverable you are</h2>
                <p className="text-muted">Your visibility score will update at the end of the week</p>
              </div>
              <div className="flex w-full flex-col items-center gap-4">
                <Button onClick={handleNext} className="w-full">
                  Next
                </Button>
                {renderStepIndicator()}
              </div>
            </div>
          </div>
        )
      case 3:
        return (
          <div className="flex flex-col items-center space-y-10 px-6 py-10">
            <div className="flex flex-col items-center space-y-6 px-6">
              <h2 className="text-center text-3xl font-bold">Earn points to increase your visibility level</h2>

              <div className="flex w-full flex-col gap-2 rounded-xl border bg-accent">
                <div className="flex items-center gap-4 border-b p-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary">
                    <ArrowCycle className="size-6 text-white" />
                  </div>
                  <span className="text-base font-medium">Referrals exchanged</span>
                </div>
                <div className="flex items-center gap-4 border-b p-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary">
                    <Users className="size-6 text-white" />
                  </div>
                  <span className="text-base font-medium">Verified Invites</span>
                </div>
                <div className="flex items-center gap-4 border-b p-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary">
                    <Heart className="size-6 text-white" />
                  </div>
                  <span className="text-base font-medium">Feed interactions</span>
                </div>
                <div className="flex items-center gap-4 p-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-partnerMatch">
                    <Star className="size-6 text-white" />
                  </div>
                  <span className="text-base font-medium">Match Of The Week conversations</span>
                </div>
              </div>

              <div className="flex w-full flex-col items-center gap-4">
                <Button onClick={handleNext} className="w-full">
                  Next
                </Button>
                {renderStepIndicator()}
              </div>
            </div>
          </div>
        )
      case 4:
        return (
          <div className="flex flex-col items-center space-y-10 px-6 py-10">
            <div className="flex flex-col items-center space-y-6 px-6">
              <div className="flex flex-col items-center gap-8 text-center">
                <h2 className="text-2xl font-bold">Based on your recent activity, your score is...</h2>
                <div className="relative">
                  <VisibilityBlankDark className="size-36" />
                  <div className="absolute inset-11">
                    <span className="text-6xl font-bold text-primary-foreground">
                      {user?.visibilityScore ? user.visibilityScore : '1'}
                    </span>
                  </div>
                </div>
                {user?.visibilityScore ? (
                  <p className="text-muted">
                    {user.visibilityScore > 4
                      ? 'Keep up the great work!'
                      : 'Start earning points and increase your visibility!'}
                  </p>
                ) : (
                  <p className="text-muted">Start earning points to increase your visibility!</p>
                )}
              </div>

              <div className="flex w-full flex-col items-center gap-4">
                <Button onClick={handleNext} className="w-full">
                  Got it!
                </Button>
                {renderStepIndicator()}
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <ResponsivePopup trigger={<div />} removePadding open={open} onOpenChange={handleOpenChange} hideCloseButton>
      <div className="flex flex-col">{renderStepContent()}</div>
    </ResponsivePopup>
  )
}
